import type { ImClient } from '../../im/imClient';
import { getProto } from '../../im/pb-imports';
import { FileWsClient } from './fileWsClient';
import { FileTransferRequestError, type FileTransferStatus, type FileTransferTask, type FileTransferTransport } from './types';
import type { LogEntry } from '@shared/config/runtime';
import { legacyFileSizeError } from '@shared/media/uploadPolicy';
import { getWebDeviceId } from '../../shared/utils/device';

type FileTransferManagerOptions = {
  onLog?: (message: string, kind?: LogEntry['kind']) => void;
  /**
   * Resolves a server-issued scoped file ticket. The manager never derives a
   * ticket from task/user fields and treats a missing result as unauthenticated.
   */
  requestFileTicket?: (input: {
    taskId: string;
    userId: number;
    fileRole: number;
    deviceId: string;
  }) => Promise<string>;
};

type ManagerBinding = {
  client: ImClient;
  userId: number;
  wsUrl: string;
};

type InternalTask = {
  snapshot: FileTransferTask;
  file?: File;
  chunks?: Uint8Array[];
  socket?: FileWsClient;
  finalized?: boolean;
  offlineFinalizePromise?: Promise<void>;
  offlineFinalizeResolve?: () => void;
  offlineFinalizeReject?: (error: Error) => void;
};

function sessionKeyOf(sessionId: number) {
  return `1_${sessionId}`;
}

function bytesToReadableLabel(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

function triggerBrowserDownload(fileName: string, objectUrl: string) {
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName || 'download.bin';
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

async function sha256OfBytes(bytes: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error('当前浏览器不支持文件完整性校验');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

function isRetryableOfflineDownload(task: InternalTask) {
  return task.snapshot.direction === 'download' && task.snapshot.transport === 'offline';
}

export class FileTransferManager {
  private binding: ManagerBinding | null = null;
  private readonly tasks = new Map<string, InternalTask>();
  private readonly listeners = new Set<(tasks: FileTransferTask[]) => void>();
  private readonly onLog: FileTransferManagerOptions['onLog'];
  private readonly requestFileTicket: FileTransferManagerOptions['requestFileTicket'];

  constructor(options: FileTransferManagerOptions = {}) {
    this.onLog = options.onLog;
    this.requestFileTicket = options.requestFileTicket;
  }

  bind(binding: ManagerBinding) {
    this.binding = binding;
  }

  reset() {
    for (const task of this.tasks.values()) {
      task.socket?.close();
      if (task.snapshot.objectUrl) {
        URL.revokeObjectURL(task.snapshot.objectUrl);
      }
    }
    this.tasks.clear();
    this.binding = null;
    this.emit();
  }

  subscribe(listener: (tasks: FileTransferTask[]) => void) {
    this.listeners.add(listener);
    listener(this.getTasks());
    return () => {
      this.listeners.delete(listener);
    };
  }

  getTasks() {
    return [...this.tasks.values()]
      .map((task) => task.snapshot)
      .sort((left, right) => right.updatedAt - left.updatedAt);
  }

  async loadOfflineFiles() {
    const { client, userId } = this.requireBinding();
    const rsp = await client.requestOfflineFiles(userId);
    const offlineFiles = rsp.getOfflineFileListList?.() ?? [];

    for (const item of offlineFiles) {
      const taskId = item.getTaskId?.() ?? '';
      if (!taskId) {
        continue;
      }

      this.upsertTask(taskId, {
        taskId,
        sessionKey: sessionKeyOf(item.getFromUserId?.() ?? 0),
        peerUserId: item.getFromUserId?.() ?? 0,
        fileName: item.getFileName?.() ?? 'file.bin',
        totalBytes: item.getFileSize?.() ?? 0,
        transferredBytes: 0,
        direction: 'download',
        transport: 'offline',
        status: 'available',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        message: null,
        canAccept: true,
        objectUrl: null,
      });
    }
  }

  handleFileNotify(notify: any) {
    const taskId = notify.getTaskId?.() ?? '';
    if (!taskId) {
      return;
    }

    const transport = this.resolveTransport(Number(notify.getTransMode?.() ?? 0));
    const offlineReady = Number(notify.getOfflineReady?.() ?? 0) === 1;
    const status: FileTransferStatus = offlineReady ? 'available' : 'waiting-peer';

    this.upsertTask(taskId, {
      taskId,
      sessionKey: sessionKeyOf(notify.getFromUserId?.() ?? 0),
      peerUserId: notify.getFromUserId?.() ?? 0,
      fileName: notify.getFileName?.() ?? 'file.bin',
      totalBytes: notify.getFileSize?.() ?? 0,
      transferredBytes: 0,
      direction: 'download',
      transport,
      status,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      message: null,
      canAccept: true,
      objectUrl: null,
    });

    this.onLog?.(
      `收到${transport === 'offline' ? '离线' : '在线'}文件：${notify.getFileName?.() ?? 'file.bin'}`,
      'info',
    );
  }

  async sendFile(file: File, toUserId: number, transport: FileTransferTransport) {
    const sizeError = legacyFileSizeError(file.size, transport === 'offline');
    if (sizeError) throw new Error(sizeError);
    const { client, userId, wsUrl } = this.requireBinding();
    const sha256 = await sha256OfBytes(await file.arrayBuffer());
    const proto = getProto();
    const requestedTransMode = transport === 'offline'
      ? proto.IM.BaseDefine.XFileType.FILE_TYPE_OFFLINE
      : proto.IM.BaseDefine.XFileType.FILE_TYPE_ONLINE;

    const fileId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const rsp = await client.requestFileTransfer(
      userId,
      toUserId,
      file.name,
      file.size,
      requestedTransMode,
      fileId,
      sha256,
    );
    const resultCode = Number(rsp.getResultCode?.() ?? -1);
    if (resultCode !== 0) {
      throw new FileTransferRequestError(resultCode);
    }

    const taskId = rsp.getTaskId?.() ?? '';
    if (!taskId) {
      throw new Error('服务端未返回 taskId');
    }

    const actualTransMode = Number(rsp.getTransMode?.() ?? requestedTransMode);
    const actualTransport: FileTransferTransport =
      actualTransMode === proto.IM.BaseDefine.XFileType.FILE_TYPE_OFFLINE
        ? 'offline'
        : 'online';
    if (actualTransport !== transport) {
      throw new Error('服务端传输模式与所选模式不一致，请重试');
    }

    let offlineFinalizePromise: Promise<void> | undefined;
    let offlineFinalizeResolve: (() => void) | undefined;
    let offlineFinalizeReject: ((error: Error) => void) | undefined;
    if (actualTransport === 'offline') {
      offlineFinalizePromise = new Promise<void>((resolve, reject) => {
        offlineFinalizeResolve = resolve;
        offlineFinalizeReject = reject;
      });
    }

    this.upsertTask(taskId, {
      taskId,
      sessionKey: sessionKeyOf(toUserId),
      peerUserId: toUserId,
      fileName: file.name,
      sha256,
      totalBytes: file.size,
      transferredBytes: 0,
      direction: 'upload',
      transport: actualTransport,
      status: 'connecting',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      message: null,
      canAccept: false,
      objectUrl: null,
    }, {
      file,
      offlineFinalizePromise,
      offlineFinalizeResolve,
      offlineFinalizeReject,
    });

    await this.startUpload(taskId, wsUrl, actualTransport);

    return this.requireTask(taskId).snapshot;
  }

  async acceptTask(taskId: string, expected?: { fileName: string; fileSize: number; sha256?: string }) {
    // The local task is only a rendering hint. The server-issued ticket and
    // authoritative lifecycle state decide whether this device may receive.
    const { wsUrl } = this.requireBinding();
    const task = this.tasks.get(taskId);
    if (!task) {
      throw new Error(`未找到文件任务: ${taskId}`);
    }
    if (task.snapshot.direction !== 'download') {
      throw new Error('仅支持接收下载任务');
    }
    if (expected) {
      this.patchTask(taskId, {
        fileName: expected.fileName,
        totalBytes: expected.fileSize,
        sha256: expected.sha256,
      });
    }
    await this.startDownload(taskId, wsUrl);
  }

  cancelTask(taskId: string, message = '在线传输已取消') {
    const task = this.tasks.get(taskId);
    if (!task || task.finalized) return;
    const { userId } = this.requireBinding();
    const proto = getProto();
    try {
      task.socket?.sendState(
        taskId,
        userId,
        proto.IM.BaseDefine.ClientFileState.CLIENT_FILE_CANCEL,
      );
    } catch {
      // Closing the socket still releases this device's connection.
    }
    this.failTask(taskId, message);
  }

  private async startUpload(taskId: string, wsUrl: string, transport: FileTransferTransport) {
    const { userId } = this.requireBinding();
    const proto = getProto();
    const task = this.requireTask(taskId);
    const file = task.file;
    if (!file) {
      throw new Error('缺少待上传文件');
    }

    const role = transport === 'offline'
      ? proto.IM.BaseDefine.ClientFileRole.CLIENT_OFFLINE_UPLOAD
      : proto.IM.BaseDefine.ClientFileRole.CLIENT_REALTIME_SENDER;
    const stateEnum = proto.IM.BaseDefine.ClientFileState;
    const socket = new FileWsClient(wsUrl, {
      onPullDataReq: async (req: any) => {
        try {
          const offset = Number(req.getOffset?.() ?? 0);
          const size = Number(req.getDataSize?.() ?? 0);
          this.onLog?.(`发送端收到分片请求：task=${taskId} offset=${offset} size=${size}`, 'info');
          const bytes = new Uint8Array(await file.slice(offset, offset + size).arrayBuffer());
          this.onLog?.(`发送端回传分片：task=${taskId} offset=${offset} bytes=${bytes.length}`, 'info');
          socket.sendChunk(taskId, userId, offset, bytes);
          this.patchTask(taskId, {
            status: 'transferring',
            transferredBytes: Math.max(task.snapshot.transferredBytes, offset + bytes.length),
          });
        } catch (error) {
          this.failTask(taskId, `上传分片失败: ${String(error)}`);
        }
      },
      onState: async (state: any) => {
        const currentState = Number(state.getState?.() ?? -1);
        this.onLog?.(`发送端收到文件状态：task=${taskId} state=${currentState}`, 'info');
        if (currentState === stateEnum.CLIENT_FILE_PEER_READY) {
          this.patchTask(taskId, { status: 'waiting-peer' });
          return;
        }
        if (currentState === stateEnum.CLIENT_FILE_DONE) {
          if (transport === 'offline') {
            await this.finalizeOfflineUpload(taskId);
            return;
          }
          this.completeTask(taskId, `${file.name} 上传完成`);
          return;
        }
        if (currentState === stateEnum.CLIENT_FILE_CANCEL) {
          this.failTask(taskId, '对方取消了文件传输');
          return;
        }
        if (currentState === stateEnum.CLIENT_FILE_REFUSE) {
          this.failTask(taskId, '对方拒绝了文件传输');
        }
      },
      onClose: () => {
        const current = this.tasks.get(taskId);
        if (current && !current.finalized && current.snapshot.status !== 'completed') {
          this.failTask(taskId, '文件传输连接已关闭');
        }
      },
    });

    task.socket = socket;
    const deviceId = task.snapshot.deviceId ?? getWebDeviceId();
    const authTicket = task.snapshot.authTicket || (transport === 'online' && this.requestFileTicket
      ? await this.requestFileTicket({ taskId, userId, fileRole: role, deviceId })
      : '');
    if (authTicket && authTicket !== task.snapshot.authTicket) {
      this.patchTask(taskId, { authTicket, deviceId });
    }
    await socket.connectAndLogin(userId, taskId, role, authTicket, deviceId);
    this.patchTask(taskId, {
      status: transport === 'offline' ? 'transferring' : 'waiting-peer',
      message: null,
    });
  }

  private async startDownload(taskId: string, wsUrl: string) {
    const { client, userId } = this.requireBinding();
    const proto = getProto();
    const task = this.requireTask(taskId);
    if (task.snapshot.objectUrl) {
      URL.revokeObjectURL(task.snapshot.objectUrl);
    }
    task.socket?.close();
    task.socket = undefined;
    task.chunks = [];
    task.finalized = false;
    const role = task.snapshot.transport === 'offline'
      ? proto.IM.BaseDefine.ClientFileRole.CLIENT_OFFLINE_DOWNLOAD
      : proto.IM.BaseDefine.ClientFileRole.CLIENT_REALTIME_RECVER;
    const stateEnum = proto.IM.BaseDefine.ClientFileState;
    const transMode = task.snapshot.transport === 'offline'
      ? proto.IM.BaseDefine.XFileType.FILE_TYPE_OFFLINE
      : proto.IM.BaseDefine.XFileType.FILE_TYPE_ONLINE;
    let requestedOffset = 0;

    const requestNextChunk = () => {
      const current = this.requireTask(taskId);
      const remaining = current.snapshot.totalBytes - requestedOffset;
      if (remaining <= 0) {
        current.socket?.sendState(taskId, userId, stateEnum.CLIENT_FILE_DONE);
        return;
      }
      current.socket?.requestChunk(taskId, userId, transMode, requestedOffset, Math.min(64 * 1024, remaining));
    };

    const socket = new FileWsClient(wsUrl, {
      onPullDataRsp: async (rsp: any) => {
        try {
          const data = rsp.getData_asU8?.() ?? new Uint8Array();
          const offset = Number(rsp.getOffset?.() ?? 0);
          const current = this.requireTask(taskId);
          current.chunks ??= [];
          current.chunks.push(data);
          requestedOffset = Math.max(requestedOffset, offset + data.length);
          this.patchTask(taskId, {
            status: 'transferring',
            transferredBytes: requestedOffset,
          });

          if (requestedOffset >= current.snapshot.totalBytes) {
            if (current.snapshot.transport === 'offline') {
              void client.deleteOfflineFileRecord(current.snapshot.peerUserId, userId, taskId).catch((error: unknown) => {
                this.onLog?.(`删除离线文件记录失败: ${String(error)}`, 'warn');
              });
            }
            socket.sendState(taskId, userId, stateEnum.CLIENT_FILE_DONE);
            await this.finishDownload(taskId);
            return;
          }

          requestNextChunk();
        } catch (error) {
          this.failTask(taskId, `下载分片失败: ${String(error)}`);
        }
      },
      onState: async (state: any) => {
        const currentState = Number(state.getState?.() ?? -1);
        if (currentState === stateEnum.CLIENT_FILE_PEER_READY) {
          this.patchTask(taskId, {
            status: task.snapshot.transport === 'offline' ? 'available' : 'transferring',
            canAccept: false,
          });
          if (task.snapshot.transport === 'online' && requestedOffset === 0) {
            requestNextChunk();
          }
          return;
        }
        if (currentState === stateEnum.CLIENT_FILE_DONE) {
          await this.finishDownload(taskId);
          return;
        }
        if (currentState === stateEnum.CLIENT_FILE_CANCEL) {
          this.failTask(taskId, '对方取消了文件传输');
          return;
        }
        if (currentState === stateEnum.CLIENT_FILE_REFUSE) {
          this.failTask(taskId, '对方拒绝了文件传输');
        }
      },
      onClose: () => {
        const current = this.tasks.get(taskId);
        if (current && !current.finalized && current.snapshot.status !== 'completed') {
          this.failTask(taskId, '文件传输连接已关闭');
        }
      },
    });

    task.socket = socket;
    const deviceId = task.snapshot.deviceId ?? getWebDeviceId();
    const authTicket = task.snapshot.authTicket || (task.snapshot.transport === 'online' && this.requestFileTicket
      ? await this.requestFileTicket({ taskId, userId, fileRole: role, deviceId })
      : '');
    if (authTicket && authTicket !== task.snapshot.authTicket) {
      this.patchTask(taskId, { authTicket, deviceId });
    }
    await socket.connectAndLogin(userId, taskId, role, authTicket, deviceId);
    this.patchTask(taskId, {
      status: task.snapshot.transport === 'offline' ? 'transferring' : 'waiting-peer',
      transferredBytes: 0,
      canAccept: false,
      message: null,
      objectUrl: null,
    });

    if (task.snapshot.transport === 'offline') {
      requestNextChunk();
    }
  }

  private async finishDownload(taskId: string) {
    const task = this.requireTask(taskId);
    if (task.finalized) {
      return;
    }
    const blob = new Blob(task.chunks ?? [], { type: 'application/octet-stream' });
    if (blob.size !== task.snapshot.totalBytes) {
      this.failTask(taskId, '文件大小校验失败，请重新接收');
      return;
    }
    if (task.snapshot.sha256) {
      const actualHash = await sha256OfBytes(await blob.arrayBuffer());
      if (actualHash !== task.snapshot.sha256) {
        this.failTask(taskId, '文件完整性校验失败，请重新接收');
        return;
      }
    }
    task.finalized = true;
    const objectUrl = URL.createObjectURL(blob);
    triggerBrowserDownload(task.snapshot.fileName, objectUrl);
    this.patchTask(taskId, {
      status: 'completed',
      transferredBytes: task.snapshot.totalBytes,
      objectUrl,
      message: '文件已保存到浏览器下载目录',
      canAccept: false,
    });
    task.socket?.close();
    task.socket = undefined;
    this.onLog?.(
      `文件下载完成：${task.snapshot.fileName}（${bytesToReadableLabel(task.snapshot.totalBytes)}）`,
      'success',
    );
  }

  private completeTask(taskId: string, message: string) {
    const task = this.requireTask(taskId);
    if (task.finalized) {
      return;
    }
    task.finalized = true;
    this.patchTask(taskId, {
      status: 'completed',
      transferredBytes: task.snapshot.totalBytes,
      message,
      canAccept: false,
    });
    task.socket?.close();
    task.socket = undefined;
    task.offlineFinalizeResolve?.();
    task.offlineFinalizeResolve = undefined;
    task.offlineFinalizeReject = undefined;
    this.onLog?.(message, 'success');
  }

  private failTask(taskId: string, message: string) {
    const task = this.tasks.get(taskId);
    if (!task || task.finalized) {
      return;
    }
    const retryableOfflineDownload = isRetryableOfflineDownload(task);
    task.finalized = true;
    task.socket?.close();
    task.socket = undefined;
    task.chunks = [];
    task.offlineFinalizeReject?.(new Error(message));
    task.offlineFinalizeResolve = undefined;
    task.offlineFinalizeReject = undefined;
    this.patchTask(taskId, {
      status: 'failed',
      transferredBytes: retryableOfflineDownload ? 0 : task.snapshot.transferredBytes,
      message: retryableOfflineDownload ? `${message}，可重新接收` : message,
      canAccept: retryableOfflineDownload,
    });
    this.onLog?.(message, 'error');
  }

  private async finalizeOfflineUpload(taskId: string) {
    const task = this.requireTask(taskId);
    if (task.finalized) {
      return;
    }

    try {
      const { client, userId } = this.requireBinding();
      const rsp = await client.addOfflineFileRecord(
        userId,
        task.snapshot.peerUserId,
        taskId,
        task.snapshot.fileName,
        task.snapshot.totalBytes,
      );
      const resultCode = Number(rsp.getResultCode?.() ?? -1);
      if (resultCode !== 0) {
        throw new Error(`离线文件登记失败: ${resultCode}`);
      }
      this.completeTask(taskId, `${task.snapshot.fileName} 上传完成`);
    } catch (error) {
      this.failTask(taskId, `离线文件登记失败: ${String(error)}`);
    }
  }

  private resolveTransport(transMode: number): FileTransferTransport {
    const proto = getProto();
    return transMode === proto.IM.BaseDefine.XFileType.FILE_TYPE_OFFLINE
      ? 'offline'
      : 'online';
  }

  private requireBinding() {
    if (!this.binding) {
      throw new Error('文件传输服务尚未绑定 IM 会话');
    }
    return this.binding;
  }

  private requireTask(taskId: string) {
    const task = this.tasks.get(taskId);
    if (!task) {
      throw new Error(`未找到文件任务: ${taskId}`);
    }
    return task;
  }

  private upsertTask(snapshot: FileTransferTask, state?: Partial<InternalTask>): void;
  private upsertTask(taskId: string, snapshot: FileTransferTask, state?: Partial<InternalTask>): void;
  private upsertTask(
    taskOrId: string | FileTransferTask,
    maybeSnapshot?: FileTransferTask | Partial<InternalTask>,
    maybeState?: Partial<InternalTask>,
  ) {
    const snapshot = typeof taskOrId === 'string' ? maybeSnapshot as FileTransferTask : taskOrId;
    const state = typeof taskOrId === 'string' ? maybeState : maybeSnapshot as Partial<InternalTask> | undefined;
    const existing = this.tasks.get(snapshot.taskId);
    this.tasks.set(snapshot.taskId, {
      snapshot: existing
        ? {
          ...existing.snapshot,
          ...snapshot,
          updatedAt: snapshot.updatedAt || Date.now(),
        }
        : snapshot,
      file: state?.file ?? existing?.file,
      chunks: state?.chunks ?? existing?.chunks,
      socket: state?.socket ?? existing?.socket,
      finalized: state?.finalized ?? existing?.finalized ?? false,
      offlineFinalizePromise: state?.offlineFinalizePromise ?? existing?.offlineFinalizePromise,
      offlineFinalizeResolve: state?.offlineFinalizeResolve ?? existing?.offlineFinalizeResolve,
      offlineFinalizeReject: state?.offlineFinalizeReject ?? existing?.offlineFinalizeReject,
    });
    this.emit();
  }

  private patchTask(taskId: string, patch: Partial<FileTransferTask>) {
    const current = this.requireTask(taskId);
    current.snapshot = {
      ...current.snapshot,
      ...patch,
      updatedAt: Date.now(),
    };
    this.emit();
  }

  private emit() {
    const snapshot = this.getTasks();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}
